/**
 * Passphrase-encrypted wrapper around a chat export. Wraps the
 * already-formatted plaintext bytes (plain text OR JSON Lines) at the
 * disk layer; the post-E2EE-decryption plaintext is what gets sealed,
 * since the export feature exists to give the user a readable copy
 * of their own messages.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatExportPassphraseSeal.swift`
 * in structure (magic prefix, fresh salt + nonce per seal, AAD bound
 * by GCM tag). Web swaps HKDF-SHA256 (iOS) for PBKDF2-SHA256 (OWASP
 * 2023 web-recommended KDF) at the spec-mandated 600_000 iteration
 * count — this is the kaether-web convention for any
 * passphrase-derived key (see `SecureValueRecovery.ts`).
 *
 * MAGIC PREFIX
 *   `"kaether-chat-export-v1"` — DISTINCT from the local-backup
 *   magic `"kaether-backup-v1"` so the decoder can refuse a
 *   cross-magic input. The self-test exercises both directions.
 *
 * FILE LAYOUT (bytes, big-endian where applicable):
 *   0..21    magic   ("kaether-chat-export-v1", 22 bytes)
 *   22..23   version (2 bytes, currently 0x00 0x01)
 *   24..39   salt    (16 bytes — PBKDF2 salt)
 *   40..51   nonce   (12 bytes — AES-GCM IV)
 *   52..59   sealedAt big-endian uint64 of unix milliseconds (8 bytes)
 *   60..end  AES-GCM ciphertext-with-tag (ciphertext | 16-byte tag)
 *
 * AAD = magic || version || sealedAt-big-endian (22 + 2 + 8 = 32 bytes).
 *   Salt + nonce are NOT in AAD — they're inputs to the key/cipher,
 *   so flipping them changes the derived key / cipher and the tag
 *   mismatches implicitly. Timestamp IS in AAD so a roll-back of
 *   the sealedAt field surfaces as auth failure.
 *
 * THREAT MODEL
 *   * Stolen file: PBKDF2-SHA256 at 600_000 iterations makes each
 *     guess expensive. Production parity with Signal-strength backups
 *     would use Argon2id; matching the parallel SVR pattern lets a
 *     future WASM-Argon2 swap land everywhere together.
 *   * Same passphrase reused across exports: every seal uses a fresh
 *     random 16-byte salt and a fresh random 12-byte nonce. Two seals
 *     of the same plaintext + passphrase produce DIFFERENT ciphertexts.
 *   * Cross-magic decode: the decoder rejects any file that does not
 *     begin with the chat-export magic, so a local-backup file
 *     (`"kaether-backup-v1"`) cannot be opened as a chat export and
 *     vice versa. Asserted in the self-test.
 */

/** File magic. DISTINCT from the local-backup magic so the decoders
 *  refuse cross-fed input. 22 bytes UTF-8. */
export const CHAT_EXPORT_SEAL_MAGIC = "kaether-chat-export-v1" as const;

/** Version pad. Reserved for future format evolution. */
const VERSION_PAD = new Uint8Array([0x00, 0x01]);

/** PBKDF2 iteration count. Matches `PRODUCTION_KDF_PARAMS` in SVR. */
const KDF_ITERATIONS = 600_000;

/** PBKDF2 salt length, bytes. */
const SALT_BYTES = 16;

/** AES-GCM nonce length, bytes. */
const NONCE_BYTES = 12;

/** AES-GCM tag length, bytes. */
const GCM_TAG_BYTES = 16;

/** Length of the on-disk header preceding the ciphertext. */
const HEADER_BYTES =
  CHAT_EXPORT_SEAL_MAGIC.length + VERSION_PAD.length + SALT_BYTES + NONCE_BYTES + 8;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown by `open()` for any auth failure (wrong passphrase, flipped
 * byte, magic mismatch, truncated file). Distinguishing "wrong
 * passphrase" from "tampered ciphertext" requires server-side metadata
 * we do not have, so we surface a single error and the UI says
 * "Wrong passphrase, or file is corrupted."
 */
export class ChatExportOpenError extends Error {
  constructor(message = "Failed to open chat export — wrong passphrase or corrupted file") {
    super(message);
    this.name = "ChatExportOpenError";
  }
}

/** Thrown by `seal()` if SubtleCrypto itself rejects the inputs. */
export class ChatExportSealError extends Error {
  constructor(message = "Failed to seal chat export") {
    super(message);
    this.name = "ChatExportSealError";
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Generate `n` random bytes via `crypto.getRandomValues`. */
function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return buf;
}

/** Encode a string as UTF-8 bytes. */
function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/** Encode a 64-bit unsigned integer big-endian. */
function uint64BigEndian(n: number): Uint8Array {
  const out = new Uint8Array(8);
  // JavaScript numbers are double-precision; safe up to 2^53. For our
  // use (unix milliseconds), this is good through year 287,396 CE.
  const high = Math.floor(n / 0x100000000);
  const low = n >>> 0;
  out[0] = (high >>> 24) & 0xff;
  out[1] = (high >>> 16) & 0xff;
  out[2] = (high >>> 8) & 0xff;
  out[3] = high & 0xff;
  out[4] = (low >>> 24) & 0xff;
  out[5] = (low >>> 16) & 0xff;
  out[6] = (low >>> 8) & 0xff;
  out[7] = low & 0xff;
  return out;
}

/** Decode a 64-bit big-endian unsigned integer. */
function uint64FromBigEndian(bytes: Uint8Array): number {
  const high =
    (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  const low =
    ((bytes[4] << 24) >>> 0) |
    (bytes[5] << 16) |
    (bytes[6] << 8) |
    bytes[7];
  return high * 0x100000000 + (low >>> 0);
}

/** Constant-time-ish byte compare (length-checked, then byte-by-byte). */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

/** Concatenate Uint8Arrays into a single contiguous buffer. */
function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/**
 * Derive a 256-bit AES-GCM key from the passphrase + salt using
 * PBKDF2-SHA256 at the production iteration count.
 */
async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    utf8(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: KDF_ITERATIONS,
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Build the canonical AAD bytes covered by the GCM auth tag. */
function buildAAD(sealedAtMillis: number): Uint8Array {
  return concatBytes(
    utf8(CHAT_EXPORT_SEAL_MAGIC),
    VERSION_PAD,
    uint64BigEndian(sealedAtMillis),
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Seal `plaintext` under `passphrase`. The plaintext is the bytes of
 * the formatted export (plain text UTF-8 OR JSON Lines UTF-8); the
 * caller chooses which.
 *
 * @param plaintext   Raw bytes to encrypt.
 * @param passphrase  User-supplied passphrase. Used as the PBKDF2 input
 *                    material verbatim — no normalisation. The UI must
 *                    enforce its own strength gate before calling.
 * @param sealedAt    Unix milliseconds. Defaults to `Date.now()`. Pass
 *                    an explicit value to make tests deterministic.
 *                    Note: even with `sealedAt` pinned, salt + nonce
 *                    are random per call, so two seals still differ.
 * @returns           Self-contained file bytes — magic, version,
 *                    salt, nonce, sealedAt, ciphertext-with-tag.
 */
export async function seal(
  plaintext: Uint8Array,
  passphrase: string,
  sealedAt: number = Date.now(),
): Promise<Uint8Array> {
  try {
    const magic = utf8(CHAT_EXPORT_SEAL_MAGIC);
    const salt = randomBytes(SALT_BYTES);
    const nonce = randomBytes(NONCE_BYTES);
    const sealedAtBytes = uint64BigEndian(sealedAt);
    const aad = buildAAD(sealedAt);

    const key = await deriveKey(passphrase, salt);
    const ciphertextWithTag = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        key,
        plaintext,
      ),
    );

    return concatBytes(magic, VERSION_PAD, salt, nonce, sealedAtBytes, ciphertextWithTag);
  } catch (e) {
    throw new ChatExportSealError(
      `seal failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

/**
 * Inverse of `seal`. Throws `ChatExportOpenError` on any failure
 * (wrong passphrase, flipped byte, magic mismatch, truncated file).
 * Never returns garbage on a wrong passphrase — AES-GCM auth tag is
 * the integrity gate.
 *
 * @param fileBytes   The on-disk file bytes produced by `seal`.
 * @param passphrase  The same passphrase used by `seal`.
 * @returns           The original plaintext bytes.
 */
export async function open(
  fileBytes: Uint8Array,
  passphrase: string,
): Promise<Uint8Array> {
  if (fileBytes.length < HEADER_BYTES + GCM_TAG_BYTES) {
    throw new ChatExportOpenError("file too short");
  }

  let cursor = 0;

  // Magic.
  const magic = utf8(CHAT_EXPORT_SEAL_MAGIC);
  const magicSlice = fileBytes.subarray(cursor, cursor + magic.length);
  if (!bytesEqual(magicSlice, magic)) {
    throw new ChatExportOpenError("magic mismatch");
  }
  cursor += magic.length;

  // Version pad.
  const padSlice = fileBytes.subarray(cursor, cursor + VERSION_PAD.length);
  if (!bytesEqual(padSlice, VERSION_PAD)) {
    throw new ChatExportOpenError("version pad mismatch");
  }
  cursor += VERSION_PAD.length;

  // Salt.
  const salt = fileBytes.subarray(cursor, cursor + SALT_BYTES);
  cursor += SALT_BYTES;

  // Nonce.
  const nonce = fileBytes.subarray(cursor, cursor + NONCE_BYTES);
  cursor += NONCE_BYTES;

  // SealedAt.
  const sealedAtBytes = fileBytes.subarray(cursor, cursor + 8);
  const sealedAt = uint64FromBigEndian(sealedAtBytes);
  cursor += 8;

  // Ciphertext + tag.
  const ciphertextWithTag = fileBytes.subarray(cursor);
  if (ciphertextWithTag.length < GCM_TAG_BYTES) {
    throw new ChatExportOpenError("ciphertext truncated");
  }

  const aad = buildAAD(sealedAt);

  try {
    const key = await deriveKey(passphrase, salt);
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, additionalData: aad },
      key,
      ciphertextWithTag,
    );
    return new Uint8Array(plaintext);
  } catch {
    // GCM auth failure is the single error surface — see class docstring.
    throw new ChatExportOpenError();
  }
}
